import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import FormSmallTextField from "../../../../../../views/components/common/form/FormTextField";

function toArchiveErrorMessage(error: unknown): string {
  if (typeof error === "string" && error.trim()) {
    return error;
  }
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }
  return "Archive checks failed";
}

export function normalizeArchiveCheckErrors(errors: unknown): string[] {
  if (!Array.isArray(errors) || errors.length === 0) {
    return [];
  }
  return errors.map(toArchiveErrorMessage);
}

interface OpsSyncForceArchivePopupProps {
  open: boolean;
  orderId: number | null;
  errors: string[];
  submitting?: boolean;
  canForceArchive?: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}

const OpsSyncForceArchivePopup = ({
  open,
  orderId,
  errors,
  submitting = false,
  canForceArchive = false,
  onClose,
  onSubmit,
}: OpsSyncForceArchivePopupProps) => {
  const theme = useTheme();
  const [reason, setReason] = useState("");

  const purple = theme?.palette?.customColors?.purple || "#9C27B0";
  const grey = theme?.palette?.customColors?.greyTextColor || "#9E9E9E";
  const pinkDark = theme?.palette?.customColors?.pinkDark || "#E91E63";
  const warning = theme?.palette?.warning?.main || "#ED6C02";
  const errorMain = theme?.palette?.error?.main || "#D32F2F";
  const isDark = theme.palette.mode === "dark";
  const trimmedReason = reason.trim();

  useEffect(() => {
    if (open) {
      setReason("");
    }
  }, [open]);

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={submitting ? undefined : onClose}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          },
        },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${purple}18 0%, ${purple}06 100%)`,
          borderBottom: `1px solid ${purple}22`,
          px: 2.5,
          pt: 2,
          pb: 2,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          disabled={submitting}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
            "&:hover": {
              bgcolor: isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.10)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>

        <Box display="flex" alignItems="center" gap={1} pr={4}>
          <ArchiveOutlinedIcon
            sx={{ color: purple, fontSize: 20, flexShrink: 0 }}
          />
          <Box>
            <CustomTypography
              variant="heading5"
              style={{ marginBottom: "4px" }}
            >
              Force Archive Order
            </CustomTypography>
            <CustomTypography variant="heading9" color={grey}>
              #{orderId}
            </CustomTypography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            gap: 1.25,
            px: 1.75,
            py: 1.5,
            mb: 2,
            borderRadius: "10px",
            border: `1px solid ${warning}40`,
            backgroundColor: `${warning}12`,
          }}
        >
          <WarningAmberRoundedIcon
            sx={{ color: warning, fontSize: 20, mt: "1px", flexShrink: 0 }}
          />
          <Box>
            <CustomTypography
              variant="heading8"
              fontWeight={700}
              style={{ marginBottom: 4 }}
            >
              Archive checks failed
            </CustomTypography>
            <CustomTypography variant="heading9" color={grey}>
              Review the issues below
              {canForceArchive
                ? " and add a reason to force archive."
                : "."}
            </CustomTypography>
          </Box>
        </Box>

        {!canForceArchive && (
          <Box
            sx={{
              px: 1.75,
              py: 1.25,
              mb: 2,
              borderRadius: "10px",
              border: `1px solid ${errorMain}35`,
              backgroundColor: `${errorMain}10`,
            }}
          >
            <CustomTypography
              variant="heading9"
              style={{ color: errorMain, fontWeight: 600 }}
            >
              You do not have permission to archive an incomplete order.
            </CustomTypography>
          </Box>
        )}

        <Box
          sx={{
            mb: 2.5,
            px: 1.75,
            py: 1.5,
            borderRadius: "10px",
            border: `1px solid ${
              isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.08)"
            }`,
            bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
          }}
        >
          <CustomTypography
            variant="heading8"
            fontWeight={700}
            style={{ marginBottom: 10 }}
          >
            Failed checks
          </CustomTypography>
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 2.25,
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            {errors.map((error, index) => (
              <Box component="li" key={`${error}-${index}`}>
                <CustomTypography
                  variant="heading9"
                  style={{
                    color: theme?.palette?.customColors?.blackBorder,
                    lineHeight: 1.45,
                  }}
                >
                  {error}
                </CustomTypography>
              </Box>
            ))}
          </Box>
        </Box>

        <CustomTypography
          variant="heading8"
          fontWeight={700}
          style={{ marginBottom: 8 }}
        >
          Force archive reason
          {canForceArchive ? (
            <span style={{ color: pinkDark }}> *</span>
          ) : null}
        </CustomTypography>
        <FormSmallTextField
          label="Explain why this order should be archived despite failed checks"
          multiline
          rows={3}
          variant="outlined"
          color="secondary"
          fontSize="14px"
          required={canForceArchive}
          value={reason}
          onChange={(_id, value) => setReason(value || "")}
          disabled={submitting || !canForceArchive}
          sx={{ marginBottom: 0 }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 1.5,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          borderTop: `1px solid ${
            isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)"
          }`,
          "& > :not(style) ~ :not(style)": { marginLeft: 0 },
        }}
      >
        <AppButton
          onClick={onClose}
          label="Cancel"
          disabled={submitting}
          customStyles={{
            height: "2.25rem",
            minWidth: "8rem",
            border: `1px solid ${pinkDark}`,
            backgroundColor: "transparent",
            color: pinkDark,
            "&:hover": { bgcolor: `${pinkDark}10` },
          }}
          variant="grayscale"
        />
        <AppButton
          onClick={() => onSubmit(trimmedReason)}
          disabled={submitting || !canForceArchive || !trimmedReason}
          label={submitting ? "Submitting..." : "Force Archive"}
          customStyles={{
            height: "2.25rem",
            minWidth: "10rem",
            background: `linear-gradient(135deg, ${purple}, ${purple}cc)`,
            color: "white",
            boxShadow: `0 3px 12px ${purple}44`,
            opacity:
              submitting || !canForceArchive || !trimmedReason ? 0.7 : 1,
          }}
          variant="primary"
        />
      </DialogActions>
    </Dialog>
  );
};

export default OpsSyncForceArchivePopup;
